import {
  AlertTriangleIcon,
  CheckCircleIcon,
  IconSizes,
  MinusCircleIcon,
  XCircleIcon,
} from '@newcore/icons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import {
  type DiagnosticStepSchema,
  DiagnosticOutcome,
} from '@/api/user-portal-api-generated';
import {
  FAILURE_REMEDIATIONS,
  NOTE_REMEDIATIONS,
  STEP_DESCRIPTIONS,
  STEP_LABELS,
  type Remediation,
} from '../constants/mcp-tester-content';
import { supportedNotes } from '../utils/verdict';
import { DocLinks } from './doc-links';

const OUTCOME_ICONS = {
  [DiagnosticOutcome.PASSED]: CheckCircleIcon,
  [DiagnosticOutcome.FAILED]: XCircleIcon,
  [DiagnosticOutcome.WARNED]: AlertTriangleIcon,
  [DiagnosticOutcome.SKIPPED]: MinusCircleIcon,
};

const OUTCOME_ICON_STYLES = {
  [DiagnosticOutcome.PASSED]: 'text-utility-green-500',
  [DiagnosticOutcome.FAILED]: 'text-error-primary',
  [DiagnosticOutcome.WARNED]: 'text-utility-amber-500',
  [DiagnosticOutcome.SKIPPED]: 'text-quaternary',
};

const OUTCOME_LABELS = {
  [DiagnosticOutcome.PASSED]: 'Passed',
  [DiagnosticOutcome.FAILED]: 'Failed',
  [DiagnosticOutcome.WARNED]: 'Worth fixing',
  [DiagnosticOutcome.SKIPPED]: 'Not reached',
};

interface RemediationBlockProps {
  remediation: Remediation;
  isFailure: boolean;
}

function RemediationBlock({ remediation, isFailure }: RemediationBlockProps) {
  return (
    <div
      className={`mt-3 flex flex-col gap-1 rounded-lg border-l-2 py-2 pl-3 ${
        isFailure
          ? 'border-error-primary bg-error-primary/5'
          : 'border-utility-amber-300 bg-utility-amber-50'
      }`}
    >
      <Text as='p' weight={FontWeights.MEDIUM} className='text-primary'>
        {remediation.summary}
      </Text>
      <Text as='p' className='text-tertiary'>
        {remediation.fix}
      </Text>
      {remediation.docs.length > 0 && <DocLinks links={remediation.docs} />}
    </div>
  );
}

interface StepRowProps {
  step: DiagnosticStepSchema;
}

export function StepRow({ step }: StepRowProps) {
  // A browser holding an older bundle can be handed a note or failure this
  // build has no copy for; rendering it would read undefined.summary.
  const failure = step.failure ? FAILURE_REMEDIATIONS[step.failure] : undefined;
  const notes = supportedNotes(step);
  const probed = step.probed_urls ?? [];
  const isSkipped = step.outcome === DiagnosticOutcome.SKIPPED;
  // An older bundle rendering <undefined /> would unmount the whole report.
  const OutcomeIcon = OUTCOME_ICONS[step.outcome] ?? MinusCircleIcon;
  // Keyed off the outcome, not off whether this build has copy for it: an
  // older bundle must still show the addresses it could not explain.
  const showProbed =
    probed.length > 0 &&
    (step.outcome === DiagnosticOutcome.FAILED ||
      step.outcome === DiagnosticOutcome.WARNED);

  return (
    <li
      className='border-secondary flex gap-3 border-b py-4 last:border-b-0'
      data-testid={`mcp-tester-step-${step.step}`}
    >
      <OutcomeIcon
        size={IconSizes.MD}
        className={`mt-0.5 shrink-0 ${OUTCOME_ICON_STYLES[step.outcome]}`}
        aria-hidden='true'
      />
      <div className='min-w-0 flex-1'>
        <Text
          as='p'
          weight={FontWeights.MEDIUM}
          className={isSkipped ? 'text-tertiary' : 'text-primary'}
        >
          {STEP_LABELS[step.step]}
        </Text>
        <Text as='p' size={TextSizes.SM} className='text-tertiary'>
          {isSkipped
            ? 'Not reached, because an earlier step failed.'
            : STEP_DESCRIPTIONS[step.step]}
        </Text>

        {failure && <RemediationBlock remediation={failure} isFailure />}
        {notes.map(note => (
          <RemediationBlock
            key={note}
            remediation={NOTE_REMEDIATIONS[note]}
            isFailure={false}
          />
        ))}

        {showProbed && (
          <div className='pt-3'>
            <Text as='p' size={TextSizes.XS} className='text-tertiary'>
              Addresses checked
            </Text>
            {probed.map(url => (
              <Text
                as='p'
                key={url}
                size={TextSizes.XS}
                className='text-tertiary break-all font-mono'
              >
                {url}
              </Text>
            ))}
          </div>
        )}
      </div>
      <Text
        as='p'
        size={TextSizes.XS}
        className='text-tertiary shrink-0'
        aria-label={`Result: ${OUTCOME_LABELS[step.outcome]}`}
      >
        {OUTCOME_LABELS[step.outcome]}
      </Text>
    </li>
  );
}
